/**
 * Configuration TanStack Query (React Query)
 * Solution standard de l'industrie pour la gestion des données serveur
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Configuration optimisée du QueryClient
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Durée de fraîcheur des données (5 minutes)
      staleTime: 5 * 60 * 1000,
      
      // Durée de cache (30 minutes)
      gcTime: 30 * 60 * 1000,
      
      // Nombre de tentatives en cas d'échec
      retry: (failureCount, error: any) => {
        // Ne pas réessayer pour les erreurs 4xx (sauf 408 et 429)
        if (error?.status >= 400 && error?.status < 500) {
          if (error.status !== 408 && error.status !== 429) {
            return false;
          }
        }
        return failureCount < 3;
      },
      
      // Délai entre les tentatives (exponentiel)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch automatique quand la fenêtre reprend le focus
      refetchOnWindowFocus: true,
      
      // Refetch quand la connexion réseau est rétablie
      refetchOnReconnect: true,
      
      // Ne pas refetch automatiquement au mount si les données sont fraîches
      refetchOnMount: true,
      
      // Garder les données précédentes pendant le chargement
      placeholderData: (previousData: unknown) => previousData,
      
      // Timeout de la requête
      networkMode: 'online',
    },
    mutations: {
      // Réessayer les mutations une fois en cas d'échec réseau
      retry: 1,
      
      // Callback global pour les erreurs de mutation
      onError: (error: any) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

// Configuration des query keys pour une gestion cohérente du cache
export const queryKeys = {
  // Auth & User
  user: ['user'] as const,
  userProfile: (userId: string) => ['user', 'profile', userId] as const,
  userSettings: (userId: string) => ['user', 'settings', userId] as const,
  
  // Company
  companies: ['companies'] as const,
  company: (companyId: string) => ['companies', companyId] as const,
  companyUsers: (companyId: string) => ['companies', companyId, 'users'] as const,
  companyStats: (companyId: string) => ['companies', companyId, 'stats'] as const,
  
  // Financial Institutions
  financialInstitutions: ['financialInstitutions'] as const,
  financialInstitution: (id: string) => ['financialInstitutions', id] as const,
  
  // Subscriptions
  subscriptions: ['subscriptions'] as const,
  subscription: (id: string) => ['subscriptions', id] as const,
  subscriptionPlans: ['subscriptions', 'plans'] as const,
  
  // Billing & Payments
  billing: ['billing'] as const,
  invoices: (companyId: string) => ['billing', 'invoices', companyId] as const,
  payments: (companyId: string) => ['billing', 'payments', companyId] as const,
  pendingTransactions: ['billing', 'pending'] as const,
  
  // Products & Services
  products: ['products'] as const,
  product: (id: string) => ['products', id] as const,
  services: ['services'] as const,
  service: (id: string) => ['services', id] as const,
  
  // Cart
  cart: ['cart'] as const,
  
  // Chat & AI
  chatHistory: (sessionId: string) => ['chat', 'history', sessionId] as const,
  chatSessions: ['chat', 'sessions'] as const,
  
  // Stats & Analytics
  stats: ['stats'] as const,
  dashboardStats: (period: string) => ['stats', 'dashboard', period] as const,
  
  // Health & Status
  backendHealth: ['health', 'backend'] as const,
  networkQuality: ['health', 'network'] as const,
} as const;

// Types pour les query keys
export type QueryKeys = typeof queryKeys;

// Helper pour invalider les queries liées
export const invalidateRelatedQueries = async (
  client: QueryClient,
  baseKey: readonly unknown[]
) => {
  await client.invalidateQueries({ queryKey: baseKey });
};

// Helper pour précharger des données
export const prefetchQuery = async <T>(
  client: QueryClient,
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  staleTime?: number
) => {
  await client.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: staleTime ?? 5 * 60 * 1000,
  });
};

// Export des composants React Query
export { QueryClientProvider, ReactQueryDevtools };
